import ClientRestBase, { SendParams } from './base';
import store from 'shared/stored/store';
import { globalLoading, globalStopLoading } from 'shared/stored/Global/slice';
import _throttle from 'lodash-es/throttle';
import { getQsRedirectUrlUserLoggedOut } from 'shared/helpers/url';
import ServiceList from 'shared/services/http-client/list';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'shared/constant';

export interface StepXError extends Error {
  data: {
    errors: {
      fields: { [key: string]: string };
    };
    code: string;
  };
}

export class StepXError extends Error {
  constructor(message: string, data?: any) {
    super(message);
    this.name = 'StepXError';
    this.data = data;
  }
}

class RestService extends ClientRestBase {
  private isRefreshing = false;
  private refreshTokenPromise: Promise<any> | null = null;

  public send = (args: SendParams): Promise<any> => {

    return super
      .sendRequestHandler(args)
      .then((response) => {
        store.dispatch(globalStopLoading());

        if (200 <= response.status && response.status <= 300) {
          return { data: response.data, status: response.status };
        }

        switch (response.status) {
          case 401:
            return this.handle401AndRetry(args);
          case 403:
            throw new Error('Permission denied');
          case 500:
            throw new Error('Server errors');
        }
      })
      .catch(_throttle((err) => this.handleRequestError(args, err), 3000));
  };

  private handle401AndRetry = async (args: SendParams): Promise<any> => {
    try {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenPromise = this.refreshAccessToken();
      }

      await this.refreshTokenPromise;
      this.isRefreshing = false;
      this.refreshTokenPromise = null;

      return await super.sendRequestHandler(args);
    } catch (e) {
      this.isRefreshing = false;
      this.refreshTokenPromise = null;

      if (window.stop) {
        window.stop();
      }
      window.location.href = `/sign-in${getQsRedirectUrlUserLoggedOut()}`;
      throw new Error('Your session has been expired');
    }
  };

  private refreshAccessToken = async (): Promise<void> => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await super.sendRequestHandler({
        serviceName: ServiceList.refresh
      });

      if (response.status === 200 && response.data?.accessToken) {
        localStorage.setItem(ACCESS_TOKEN, response.data.accessToken);
        if (response.data?.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN, response.data.refreshToken);
        }
        return;
      }

      throw new Error('Failed to refresh token');
    } catch (e) {
      throw e;
    }
  };

  public logout = async (): Promise<void> => {
    try {
      await super.sendRequestHandler({
        serviceName: ServiceList.logout
      });
    } catch (e) {
      // Even if logout API fails, we should still clear local tokens
      console.warn('Logout API call failed:', e);
    } finally {
      // Clear all stored tokens
      localStorage.removeItem(ACCESS_TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);
      
      // Redirect to login page
      window.location.href = '/sign-in';
    }
  };

  private handleRequestError = (args: SendParams, e: any) => {
    if (!e?.response) {
      throw new Error('Server errors');
    }

    switch (e.response.status) {
      case 401:
        return this.handle401AndRetry(args);
      case 403:
        throw new Error('Permission denied');
      case 422:
        throw new StepXError('Bad request', e.response.data);
      case 500:
        throw new Error('Server errors');
    }
    store.dispatch(globalStopLoading());
  };
}

export default new RestService();
